/**
 * AffiliateSignUp — files an affiliate application.
 *
 * Approval is an operator step, so a successful submit grants no session: the
 * applicant is sent to the confirmation screen. The agreement is shown in a
 * scrolling panel with the acceptance checkbox inside it, so the box cannot be
 * ticked without the terms being on screen.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import {
  AFFILIATE_COUNTRIES,
  AFFILIATE_HOW_HEARD_OPTIONS,
  AFFILIATE_HOW_HEARD_OTHER,
  STORAGE_KEYS,
} from '@wlb/shared/constants';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { storage } from '@wlb/shared/platform/storage';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { affiliateHowHeardValue } from '@wlb/shared/utils/affiliateReferralCode';
import {
  validateAffiliatePassword,
  validateEmail,
} from '@wlb/shared/utils/validation';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { AffiliateAgreement } from '../features/affiliate/AffiliateAgreement';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import type { AffiliateSignUpSuccessState } from '../features/affiliate/affiliateFlowState';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import '../components/layout/authFlow.css';
import '../features/affiliate/affiliatePortal.css';

interface FieldErrors {
  email?: string;
  password?: string;
  address1?: string;
  countryId?: string;
  howHeard?: string;
  howHeardSpecify?: string;
  agreement?: string;
}

export function AffiliateSignUp() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [countryId, setCountryId] = useState('');
  const [howHeard, setHowHeard] = useState('');
  const [howHeardSpecify, setHowHeardSpecify] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  /** Campaign slug the visitor arrived through; locks and pre-fills "Other". */
  const [campaignSource, setCampaignSource] = useState('');

  useEffect(() => {
    let active = true;
    storage
      .getItem<string>(STORAGE_KEYS.AFFILIATE_SOURCE)
      .then(source => {
        if (active && typeof source === 'string' && source) setCampaignSource(source);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  // The campaign slug is the answer, so selecting "Other" fills it in rather
  // than asking the applicant to retype where they came from.
  useEffect(() => {
    if (howHeard === AFFILIATE_HOW_HEARD_OTHER && campaignSource) {
      setHowHeardSpecify(campaignSource);
    }
  }, [howHeard, campaignSource]);

  const isOther = howHeard === AFFILIATE_HOW_HEARD_OTHER;

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const emailResult = validateEmail(email);
    if (!emailResult.valid) next.email = t(emailResult.message);
    const passwordResult = validateAffiliatePassword(password);
    if (!passwordResult.valid) next.password = t(passwordResult.message);
    if (!address1.trim()) next.address1 = t('affiliate.validation.addressRequired');
    if (!countryId) next.countryId = t('affiliate.validation.countryRequired');
    if (!howHeard) next.howHeard = t('affiliate.validation.howHeardRequired');
    if (isOther && !howHeardSpecify.trim()) {
      next.howHeardSpecify = t('affiliate.validation.specifyRequired');
    }
    if (!accepted) next.agreement = t('affiliate.validation.agreementRequired');
    return next;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError('');

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      await affiliateService.register({
        email: email.trim(),
        password,
        address1: address1.trim(),
        address2: address2.trim(),
        countryId: Number(countryId),
        howDidYouHear: affiliateHowHeardValue({
          option: howHeard,
          specify: howHeardSpecify,
        }),
      });
      // The slug has done its job; keeping it would keep overriding later answers.
      await storage.removeItem(STORAGE_KEYS.AFFILIATE_SOURCE);
      const successState: AffiliateSignUpSuccessState = {
        submitted: true,
        email: email.trim(),
      };
      navigate(AFFILIATE_ROUTES.SIGN_UP_SUCCESS, { replace: true, state: successState });
    } catch (error) {
      setFormError(getUserFacingErrorMessage(error, t('affiliate.signUp.failed')));
    } finally {
      setSubmitting(false);
    }
  };

  const clearError = (field: keyof FieldErrors) => {
    setErrors(current => (current[field] ? { ...current, [field]: undefined } : current));
  };

  return (
    <AffiliatePortalLayout centered>
      <div className="auth-flow__card">
        <h1 className="auth-flow__heading">{t('affiliate.signUp.heading')}</h1>
        <p className="auth-flow__subheading">{t('affiliate.signUp.subheading')}</p>
        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError variant="banner" message={formError} id="affiliate-signup-error" />

          <label className="auth-flow__field">
            <span>{t('affiliate.signUp.emailLabel')}</span>
            <input
              className={`auth-flow__input${errors.email ? ' auth-flow__input--error' : ''}`}
              type="email"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                clearError('email');
              }}
              placeholder={t('affiliate.signUp.emailPlaceholder')}
              autoComplete="email"
              aria-invalid={Boolean(errors.email)}
              aria-describedby={errors.email ? 'affiliate-signup-email-error' : undefined}
              autoFocus
            />
            <FormError message={errors.email} id="affiliate-signup-email-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.signUp.passwordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${errors.password ? ' auth-flow__input--error' : ''}`}
              value={password}
              onChange={event => {
                setPassword(event.target.value);
                clearError('password');
              }}
              placeholder={t('affiliate.signUp.passwordPlaceholder')}
              autoComplete="new-password"
              aria-invalid={Boolean(errors.password)}
              aria-describedby={
                errors.password ? 'affiliate-signup-password-error' : undefined
              }
            />
            <FormError message={errors.password} id="affiliate-signup-password-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.signUp.address1Label')}</span>
            <input
              className={`auth-flow__input${errors.address1 ? ' auth-flow__input--error' : ''}`}
              type="text"
              value={address1}
              onChange={event => {
                setAddress1(event.target.value);
                clearError('address1');
              }}
              placeholder={t('affiliate.signUp.address1Placeholder')}
              autoComplete="address-line1"
              aria-invalid={Boolean(errors.address1)}
              aria-describedby={
                errors.address1 ? 'affiliate-signup-address1-error' : undefined
              }
            />
            <FormError message={errors.address1} id="affiliate-signup-address1-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.signUp.address2Label')}</span>
            <input
              className="auth-flow__input"
              type="text"
              value={address2}
              onChange={event => setAddress2(event.target.value)}
              placeholder={t('affiliate.signUp.address2Placeholder')}
              autoComplete="address-line2"
            />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.signUp.countryLabel')}</span>
            <select
              className={`affiliate-auth__select${
                errors.countryId ? ' affiliate-auth__select--error' : ''
              }`}
              value={countryId}
              onChange={event => {
                setCountryId(event.target.value);
                clearError('countryId');
              }}
              autoComplete="country-name"
              aria-invalid={Boolean(errors.countryId)}
              aria-describedby={
                errors.countryId ? 'affiliate-signup-country-error' : undefined
              }
            >
              <option value="">{t('affiliate.signUp.countryPlaceholder')}</option>
              {AFFILIATE_COUNTRIES.map(country => (
                <option key={country.id} value={country.id}>
                  {country.label}
                </option>
              ))}
            </select>
            <FormError message={errors.countryId} id="affiliate-signup-country-error" />
          </label>

          <label className="auth-flow__field">
            <span>{t('affiliate.howHeard.groupLabel')}</span>
            <select
              className={`affiliate-auth__select${
                errors.howHeard ? ' affiliate-auth__select--error' : ''
              }`}
              value={howHeard}
              onChange={event => {
                setHowHeard(event.target.value);
                clearError('howHeard');
              }}
              aria-invalid={Boolean(errors.howHeard)}
              aria-describedby={
                errors.howHeard ? 'affiliate-signup-howheard-error' : undefined
              }
            >
              <option value="">{t('affiliate.howHeard.placeholder')}</option>
              {AFFILIATE_HOW_HEARD_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
            <FormError message={errors.howHeard} id="affiliate-signup-howheard-error" />
          </label>

          {isOther && (
            <label className="auth-flow__field">
              <span>{t('affiliate.howHeard.specifyLabel')}</span>
              <input
                className={`auth-flow__input${
                  errors.howHeardSpecify ? ' auth-flow__input--error' : ''
                }`}
                type="text"
                value={howHeardSpecify}
                onChange={event => {
                  setHowHeardSpecify(event.target.value);
                  clearError('howHeardSpecify');
                }}
                placeholder={t('affiliate.howHeard.specifyPlaceholder')}
                readOnly={Boolean(campaignSource)}
                aria-invalid={Boolean(errors.howHeardSpecify)}
                aria-describedby={
                  errors.howHeardSpecify ? 'affiliate-signup-specify-error' : undefined
                }
              />
              <FormError
                message={errors.howHeardSpecify}
                id="affiliate-signup-specify-error"
              />
            </label>
          )}

          <section aria-labelledby="affiliate-agreement-title">
            <h2 className="affiliate-profile__section-title" id="affiliate-agreement-title">
              {t('legal.affiliateAgreement.title')}
            </h2>
            <AffiliateAgreement scrollable />
          </section>

          <label className="affiliate-auth__agreement">
            <input
              type="checkbox"
              checked={accepted}
              onChange={event => {
                setAccepted(event.target.checked);
                clearError('agreement');
              }}
              aria-invalid={Boolean(errors.agreement)}
              aria-describedby={
                errors.agreement ? 'affiliate-signup-agreement-error' : undefined
              }
            />
            {t('affiliate.signUp.agreementAccept')}
          </label>
          <FormError message={errors.agreement} id="affiliate-signup-agreement-error" />

          <button
            className={`auth-flow__primary${
              accepted ? '' : ' auth-flow__primary--blocked'
            }`}
            type="submit"
            disabled={submitting || !accepted}
          >
            {submitting ? t('common.loading') : t('affiliate.signUp.submit')}
          </button>
        </form>
        <p className="auth-flow__footer">
          {t('affiliate.signUp.haveAccount')}
          <Link to={AFFILIATE_ROUTES.SIGN_IN}>{t('common.buttons.signIn')}</Link>
        </p>
      </div>
    </AffiliatePortalLayout>
  );
}
